import { useState } from 'react'

import { Modal } from '@mantine/core'
import { ActivityLog, Applicant } from 'types/graphql'

import ActivityTimestamp from 'src/components/ActivityLogs/shared/ActivityTimestamp'
import ActorBadge from 'src/components/ActivityLogs/shared/ActorBadge'
import DocumentPreview from 'src/components/Overlays/DocumentPreview/DocumentPreview'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import {
  ActivityLogActorType,
  SMSDirection,
} from 'src/graphql/types/activityLogs'
import { DocumentUserStatus } from 'src/graphql/types/documents'
import IconAlertTriangle from 'src/icons/IconAlertTriangle'
import IconFile from 'src/icons/IconFile'
import IconSparkle from 'src/icons/IconSparkle'

interface SMSLogCardProps {
  activityLog: ActivityLog
  applicant?: Applicant
}

// Returns the highest-confidence completed prediction for a document,
// or null if none exists.
function getTopPrediction(documentIntelligence: any) {
  if (!documentIntelligence || documentIntelligence.status !== 'COMPLETED')
    return null
  const pending = documentIntelligence.predictions.filter(
    (p) =>
      !p.autoApplied &&
      p.reviewDecision !== 'ACCEPTED_AI' &&
      p.reviewDecision !== 'KEPT_ORIGINAL'
  )
  if (!pending.length) return null
  return pending.reduce((best, p) =>
    p.confidence > best.confidence ? p : best
  )
}

// Rich document card rendered inline inside an SMS bubble.
// Shows filename, a "Pending Review" badge for PENDING_REVIEW docs, and
// an AI confidence badge when document intelligence is enabled.
const SMSDocumentCard = ({
  document,
  showAi,
  onClick,
}: {
  document: any
  showAi: boolean
  onClick: () => void
}) => {
  const isPendingReview =
    document.userStatus === DocumentUserStatus.PENDING_REVIEW
  const topPred = showAi
    ? getTopPrediction(document.documentIntelligence)
    : null
  const pct = topPred ? Math.round(topPred.confidence * 100) : null

  return (
    <button
      onClick={onClick}
      className="flex w-full items-start gap-2 rounded-lg border border-doubleNickel-gray-200 bg-white px-3 py-2 text-left transition-colors hover:bg-doubleNickel-gray-50"
    >
      <div className="flex h-8 w-7 shrink-0 items-center justify-center rounded-md bg-doubleNickel-gray-100">
        <IconFile className="h-4 w-3.5 fill-none stroke-doubleNickel-gray-400" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center justify-between gap-2">
          <span className="min-w-0 flex-1 truncate text-xs font-medium text-doubleNickel-gray-700">
            {document.fileName}
          </span>
          {pct !== null && (
            <span
              className="inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none"
              style={{ backgroundColor: '#EBF5FB', color: '#1C93D7' }}
            >
              <IconSparkle size={9} />
              {pct}%
            </span>
          )}
        </div>
        {isPendingReview && (
          <span
            className="inline-flex w-fit items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium leading-none"
            style={{
              backgroundColor: '#FFFAEB',
              color: '#B54708',
              borderColor: '#FEDF89',
            }}
          >
            <IconAlertTriangle className="h-2.5 w-2.5 fill-none stroke-doubleNickel-warning-500" />
            Pending Review
          </span>
        )}
      </div>
    </button>
  )
}

const SMSLog = ({ activityLog, applicant }: SMSLogCardProps) => {
  const currentCompany = useCurrentCompanyContext()
  const showAi = currentCompany?.documentIntelligenceEnabled ?? false

  const [documentForPreview, setDocumentForPreview] = useState(null)

  let displayName = ''
  if (
    activityLog.smsDirection === SMSDirection.OUTBOUND &&
    activityLog.employee
  ) {
    displayName = `${activityLog.employee.firstName} ${activityLog.employee.lastName}`
  } else if (activityLog.smsDirection === SMSDirection.INBOUND && applicant) {
    displayName = `${applicant.firstName} ${applicant.lastName}`
  }

  // Surface how an outbound message was sent. The backend records this on
  // every log (actorType) but it was never shown, so automated- and bulk-sent
  // texts looked identical to ones a recruiter typed by hand.
  const isAutomated =
    activityLog.actorType === ActivityLogActorType.AUTOMATED_RULE ||
    activityLog.actorType === ActivityLogActorType.SYSTEM
  const isBulk = activityLog.actorType === ActivityLogActorType.BULK_ACTION

  return (
    <>
      {documentForPreview && (
        <Modal
          size="90rem"
          lockScroll={false}
          zIndex={210}
          classNames={{
            content: 'min-h-96 h-[100%]',
            body: 'flex h-[95%] flex-col overflow-auto',
          }}
          transitionProps={{ transition: 'fade', duration: 200 }}
          opened={!!documentForPreview}
          onClose={() => setDocumentForPreview(null)}
          withCloseButton={false}
        >
          <DocumentPreview
            documentId={documentForPreview.documentId}
            applicantId={applicant?.applicantId}
            showClosebutton={true}
            showBackButton={false}
            onBack={() => setDocumentForPreview(null)}
            showActions={false}
          />
        </Modal>
      )}

      <div
        className={`flex max-w-[75%] flex-col gap-1 ${
          activityLog?.smsDirection === SMSDirection.OUTBOUND
            ? 'ml-auto'
            : 'mr-auto'
        }`}
      >
        {/* Name pinned left, timestamp pinned right — uniform across SMS/Call/
            Email threads regardless of which side the bubble sits on. */}
        <div className="flex w-full flex-row items-center justify-between gap-2 text-xs text-doubleNickel-gray-600">
          {isAutomated ? (
            <ActorBadge variant="auto" label="Sent automatically by a rule" />
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="font-medium">{displayName}</span>
              {isBulk && (
                <ActorBadge
                  variant="bulk"
                  label="Sent as part of a bulk message"
                />
              )}
            </div>
          )}
          <ActivityTimestamp date={activityLog.createdAt} />
        </div>
        <div
          className={`flex flex-col gap-2 rounded-t-lg p-3 ${
            activityLog?.smsDirection === SMSDirection.OUTBOUND
              ? 'rounded-bl-xl  bg-doubleNickel-brand-25'
              : 'rounded-br-xl  bg-doubleNickel-gray-50'
          }`}
        >
          <div className="w-full overflow-hidden hyphens-auto whitespace-pre-wrap text-sm text-doubleNickel-gray-900 [overflow-wrap:anywhere]">
            {activityLog.content}
          </div>
          {activityLog?.documents && activityLog.documents.length > 0 && (
            <div className="flex flex-col gap-1.5">
              {activityLog.documents.map((doc) => (
                <SMSDocumentCard
                  key={doc.documentId}
                  document={doc}
                  showAi={showAi}
                  onClick={() => setDocumentForPreview(doc)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default SMSLog
